// src/shared/ui/ErrorBoundary.jsx
//
// The app's first React error boundary. frontend/CLAUDE.md notes there
// was none anywhere, so a thrown render error in one component blanked
// the whole page, and asks that new top-level routes be wrapped in one.
//
// This is a CLASS component, against the file's own "function components
// and hooks only" rule, because React provides no hook equivalent of
// getDerivedStateFromError/componentDidCatch — a boundary cannot be a
// function component. That is the entire exception; don't read it as
// licence for class components elsewhere.
//
// Note what it does and does not catch: render-phase and lifecycle
// errors in the subtree, yes; errors thrown inside an event handler or a
// rejected promise, no — those never reach a boundary and still need
// their own try/catch (which is why the report pages set an error state
// from their fetch .catch rather than relying on this).
//
// Usage:
//   <ErrorBoundary title="Report unavailable">
//     <SowUsageReportPage />
//   </ErrorBoundary>

import { Component } from "react";
import PropTypes from "prop-types";
import { AlertTriangle } from "lucide-react";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Deliberately console.error and not a toast: this fires during
    // render, when the component tree is already broken, and the
    // stack is what a developer needs. There is no client-side error
    // reporting service wired up in this app yet — when one lands, this
    // is the single place it hooks into.
    console.error("Unhandled render error", error, info?.componentStack);
  }

  handleRetry() {
    // Clearing the error remounts the subtree. Enough to recover from a
    // transient failure (a bad payload that a refetch fixes); if the
    // cause is deterministic the boundary simply catches it again, which
    // is better than leaving the user with a dead page and no action.
    this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    const { children, title = "Something went wrong" } = this.props;

    if (!error) return children;

    return (
      <div className="flex flex-col items-center justify-center text-center gap-2 py-16 px-6">
        <div className="mb-1 flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-status-critical">
          <AlertTriangle size={18} />
        </div>
        <p className="text-sm font-semibold text-ink">{title}</p>
        <p className="text-sm text-muted max-w-md">
          This section failed to render. The rest of the app is unaffected.
        </p>
        <button
          type="button"
          onClick={this.handleRetry}
          className="mt-3 h-9 px-3 rounded-lg bg-brand-600 text-white text-sm font-medium"
        >
          Try again
        </button>
      </div>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
};
